import { z } from 'zod';
import { uuidv7 } from 'uuidv7';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback, useEffect, useMemo } from 'react';
import { DateTime } from 'luxon';
import { useTrimBudgetApi } from '../api/use-trim-budget';
import { useTimeZone } from '../../user/api/use-get-user';
import { useBudgetSummary } from '../budget-store';
// Budget Trim Form Schema
export const budgetTrimFormSchema = z.object({
    name: z.string().min(1, 'Budget name is required'),
    year: z.number().min(1900).max(2100),
    month: z.number().min(1).max(12),
});
// Budget Trim Form Hook
export const useBudgetTrimForm = ({ budget, budgets, onSuccess }) => {
    const userTimeZone = useTimeZone();
    const budgetSummary = useBudgetSummary();
    // mutations
    const { mutate: trimBudget, isPending } = useTrimBudgetApi({ onSuccess });
    // Calculate year options based on budget history
    const yearOptions = useMemo(() => {
        const currentYear = DateTime.now().setZone(userTimeZone).year;
        const firstDate = DateTime.fromISO(budgetSummary.initialTransactionDate).setZone(userTimeZone);
        const startYear = firstDate.year;
        return Array.from({ length: currentYear - startYear + 1 }, (_, i) => startYear + i);
    }, [budgetSummary, userTimeZone]);
    // enhance schema with name uniqueness validation
    const schema = budgetTrimFormSchema.refine((data) => {
        const isNameTaken = budgets.some((b) => b.name.toLowerCase() === data.name.toLowerCase() && b.id !== budget.id);
        return !isNameTaken;
    }, {
        message: 'A budget with this name already exists',
        path: ['name'],
    });
    const defaultValues = useMemo(() => toFormData(budget, userTimeZone), [budget, userTimeZone]);
    const form = useForm({
        resolver: zodResolver(schema),
        defaultValues,
    });
    useEffect(() => {
        form.reset(defaultValues);
    }, [budget, form, defaultValues]);
    /**
     * Handles the form submission event, building the API payload and
     * invoking the trim mutation.
     *
     * @param values - Sanitized budget trim form values.
     */
    const submitForm = useCallback((values) => {
        const payload = toRequestData(values, budget);
        trimBudget(payload);
    }, [budget, trimBudget]);
    return {
        form,
        onSubmit: form.handleSubmit(submitForm),
        isSubmitting: isPending,
        yearOptions,
    };
};
/**
 * Compute the initial values for the BudgetTrimForm.
 *
 * @param budget - The budget to trim
 * @param timeZone - User's IANA timezone for date handling
 * @returns A fully populated BudgetTrimForm
 */
function toFormData(budget, timeZone) {
    // Default to January of the previous year
    const defaultDate = DateTime.now().setZone(timeZone).minus({ years: 1 }).startOf('year');
    return {
        name: `${budget.name} (Trimmed)`,
        year: defaultDate.year,
        month: defaultDate.month,
    };
}
/**
 * Build the BudgetRequestTrim payload from form values and context.
 *
 * @param formValues - The values from the budget trim form.
 * @param budget - The budget being trimmed.
 * @returns A fully populated BudgetRequestTrim for the API.
 */
function toRequestData(formValues, budget) {
    return {
        id: uuidv7(),
        budgetId: budget.id,
        name: formValues.name,
        year: formValues.year,
        month: formValues.month,
    };
}
